import { Injectable, OnDestroy } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { environment } from "environment";
import { StorageService } from "src/app/common/services/storage.service";
import {
  AnnouncementClient,
  ActiveAnnouncementApi,
} from "src/app/common/services/announcement.client";
import {
  Severity,
  SEVERITY_BY_ID,
  SEVERITY_ORDER,
  DISMISSALS_KEY,
} from "./announcement-banner-constants";

export interface Announcement {
  id: string;
  title: string;
  bannerMessage: string;
  content: string;
  safeContent: SafeHtml;
  severity: Severity;
}

const IMG_SRC_ATTRIBUTE = /src="([^"]*)"/gi;
const ANNOUNCEMENT_IMAGE_MARKER = "/announcement/image?key=";

function resolveAnnouncementImageUrls(html: string): string {
  return html.replace(IMG_SRC_ATTRIBUTE, (match, src: string) => {
    const markerIndex = src.indexOf(ANNOUNCEMENT_IMAGE_MARKER);
    if (markerIndex < 0) return match;
    return `src="${environment.endpoint}${src.slice(markerIndex)}"`;
  });
}

function fromActiveDto(
  dto: ActiveAnnouncementApi,
  sanitizer: DomSanitizer
): Announcement {
  const content = resolveAnnouncementImageUrls(dto.content);
  return {
    id: String(dto.id),
    title: dto.title,
    bannerMessage: resolveAnnouncementImageUrls(dto.bannermessage),
    content,
    safeContent: sanitizer.bypassSecurityTrustHtml(content),
    severity: SEVERITY_BY_ID[dto.severityid] ?? "Standard",
  };
}

function readDismissedIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(DISMISSALS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function writeDismissedIds(ids: Set<string>): void {
  sessionStorage.setItem(DISMISSALS_KEY, JSON.stringify(Array.from(ids)));
}

/**
 * Single source of truth for active announcements + slideout panel state,
 * shared between AnnouncementBannerComponent (banner region + overlay +
 * slideout, hosted in AppComponent) and AnnouncementBellComponent (the
 * persistent entry point, hosted inside SidenavComponent's "Menu" row).
 * Those two sit in unrelated branches of the component tree — a root-provided
 * service is what keeps them in sync, since @Input/@Output only works for a
 * direct parent/child relationship.
 */
@Injectable({ providedIn: "root" })
export class AnnouncementPanelService implements OnDestroy {
  private readonly activeSubject = new BehaviorSubject<Announcement[]>([]);
  readonly active$: Observable<Announcement[]> = this.activeSubject.asObservable();

  private readonly panelOpenSubject = new BehaviorSubject<boolean>(false);
  readonly panelOpen$: Observable<boolean> = this.panelOpenSubject.asObservable();

  private readonly panelModeSubject = new BehaviorSubject<"list" | "detail">("list");
  readonly panelMode$: Observable<"list" | "detail"> = this.panelModeSubject.asObservable();

  private readonly selectedIdSubject = new BehaviorSubject<string | null>(null);
  readonly selectedId$: Observable<string | null> = this.selectedIdSubject.asObservable();

  private readonly facilitySub: Subscription;
  private activeRequestSub: Subscription | null = null;

  constructor(
    private storageService: StorageService,
    private announcementClient: AnnouncementClient,
    private sanitizer: DomSanitizer
  ) {
    this.facilitySub = this.storageService.isSelectedFacility.subscribe(() => this.refresh());
    this.refresh();
    window.addEventListener("storage", this.onStorageEvent);
  }

  ngOnDestroy(): void {
    this.facilitySub.unsubscribe();
    this.activeRequestSub?.unsubscribe();
    window.removeEventListener("storage", this.onStorageEvent);
  }

  private onStorageEvent = (event: StorageEvent): void => {
    if (event.key === DISMISSALS_KEY) {
      this.refresh();
    }
  };

  refresh(): void {
    this.activeRequestSub?.unsubscribe();
    const facilityId = this.storageService.getSelectedFacilityId();
    this.activeRequestSub = this.announcementClient.getActive(facilityId).subscribe({
      next: (items) => this.applyActive(items.map((dto) => fromActiveDto(dto, this.sanitizer))),
      error: (err) => {
        console.error("[AnnouncementPanelService] failed to load active announcements", err);
      },
    });
  }

  private applyActive(all: Announcement[]): void {
    const dismissed = readDismissedIds();
    const active = all
      .filter((a) => !dismissed.has(a.id))
      .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
    this.activeSubject.next(active);

    const selectedId = this.selectedIdSubject.value;
    if (selectedId && !active.some((a) => a.id === selectedId)) {
      this.selectedIdSubject.next(null);
      if (this.panelModeSubject.value === "detail") this.panelModeSubject.next("list");
    }
  }

  dismiss(id: string): void {
    const dismissed = readDismissedIds();
    dismissed.add(id);
    writeDismissedIds(dismissed);
    this.activeSubject.next(this.activeSubject.value.filter((a) => a.id !== id));
    if (this.selectedIdSubject.value === id) {
      this.selectedIdSubject.next(null);
      if (this.panelModeSubject.value === "detail") this.panelModeSubject.next("list");
    }
  }

  /** Clicking a visible banner bar — jumps straight to that announcement's
   * full content, same as before this feature grew a list view. */
  openDetail(id: string): void {
    this.selectedIdSubject.next(id);
    this.panelModeSubject.next("detail");
    this.panelOpenSubject.next(true);
  }

  /** Bell icon or the "+N more" pill — both open the same card list. */
  openList(): void {
    this.panelModeSubject.next("list");
    this.panelOpenSubject.next(true);
  }

  selectFromList(id: string): void {
    this.selectedIdSubject.next(id);
    this.panelModeSubject.next("detail");
  }

  backToList(): void {
    this.panelModeSubject.next("list");
  }

  closePanel(dismissSelected: boolean): void {
    const selectedId = this.selectedIdSubject.value;
    if (dismissSelected && this.panelModeSubject.value === "detail" && selectedId) {
      this.dismiss(selectedId);
    }
    this.panelOpenSubject.next(false);
    this.selectedIdSubject.next(null);
  }
}
